import React, { useEffect, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import {
  BOARD_GROUPS,
  getBoardPosts,
  getBoardPostDetailByPublicId,
} from "../../../services/contactCenterService";
import { getFaqCategories } from "../../../services/lookupService";
import { getI18nLanguageFromLocalStorage } from "../../../helpers/localStorageHelper";
import { getLocalizedField } from "../../../helpers/localizedHelper";
import { useTranslation } from "react-i18next";
import AttachmentDownloads from "../AttachmentDownloads";

// English fallbacks for this component's common_ns keys. The backend common_ns source
// (Google Sheet -> ContentTranslation) overrides these once the keys are seeded; ko/vi
// are served from there. Keeps the component self-sufficient without a local bundle.
const DEFAULTS = {
  "CONTACT_US_CENTER.FAQ.SEARCH_PLACEHOLDER": "Search frequently asked questions.",
  "CONTACT_US_CENTER.FAQ.EMPTY": "No FAQs yet.",
  "CONTACT_US_CENTER.FAQ.CATEGORY_ALL": "All",
  "CONTACT_US_CENTER.COMMON.SEARCH": "Search",
};

const ROWS = 50;

/** FAQ (ACCORDION_VIEW) — category filter + search + Q/A accordion over the tab's FAQ posts. */
const FaqTab = ({ group, tab }) => {
  const [faqs, setFaqs] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  // Attachments per FAQ, lazily loaded from the post detail when an item is first opened
  // (the paged FAQ list only carries a count, not the files). undefined = not yet fetched.
  const [attachmentsById, setAttachmentsById] = useState({});
  // FAQ category filter (null = all). Only the Customer Center FAQ is categorized.
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(null);
  const language = getI18nLanguageFromLocalStorage();
  const { t: translate } = useTranslation("common_ns", { useSuspense: false });
  const t = (key, options) =>
    translate(key, { defaultValue: DEFAULTS[key] ?? key, ...options });

  const showCategoryFilter = group === BOARD_GROUPS.CUSTOMER_CENTER;

  // Load the FAQ category list once (Customer Center only) for the filter tab list.
  useEffect(() => {
    if (!showCategoryFilter) return;
    let alive = true;
    getFaqCategories().then((data) => {
      if (alive) setCategories(data || []);
    });
    return () => {
      alive = false;
    };
  }, [showCategoryFilter]);

  // Reset the category filter when switching to a different FAQ tab.
  useEffect(() => {
    setCategoryId(null);
  }, [tab.id]);

  useEffect(() => {
    let alive = true;
    getBoardPosts(group, tab.id, {
      first: 0,
      rows: ROWS,
      search: query,
      categoryId,
    }).then((res) => {
      if (!alive) return;
      const data = res.data || [];
      setFaqs(data);
      setOpenId(data.length ? data[0].id : null);
    });
    return () => {
      alive = false;
    };
  }, [group, tab.id, query, categoryId]);

  const onSearch = () => setQuery(search);

  // Toggle a FAQ open/closed; on first open, lazily fetch its attachments (if any) so the
  // answer can offer the same downloads as the post detail page.
  const toggleFaq = (faq) => {
    const next = openId === faq.id ? null : faq.id;
    setOpenId(next);
    if (
      next &&
      faq.attachmentCount > 0 &&
      faq.publicId &&
      attachmentsById[faq.id] === undefined
    ) {
      // Mark as loading so we don't refetch while the request is in flight.
      setAttachmentsById((prev) => ({ ...prev, [faq.id]: [] }));
      getBoardPostDetailByPublicId(group, faq.publicId).then((res) => {
        setAttachmentsById((prev) => ({ ...prev, [faq.id]: res?.attachments || [] }));
      });
    }
  };

  return (
    <div>
      {showCategoryFilter && categories.length > 0 && (
        <div className="cuc-chips">
          <button
            type="button"
            className={`cuc-chip ${categoryId === null ? "is-active" : ""}`}
            onClick={() => setCategoryId(null)}
          >
            {t("CONTACT_US_CENTER.FAQ.CATEGORY_ALL")}
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`cuc-chip ${categoryId === c.id ? "is-active" : ""}`}
              onClick={() => setCategoryId(c.id)}
            >
              {getLocalizedField(c, "name", language)}
            </button>
          ))}
        </div>
      )}

      <div className="cuc-search-row">
        <InputText
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
          placeholder={t("CONTACT_US_CENTER.FAQ.SEARCH_PLACEHOLDER")}
        />
        <Button
          className="keduall-buttons button-filled-default-normal-primary text-white-txt keduall-typography heading-h6-semibold-600"
          label={t("CONTACT_US_CENTER.COMMON.SEARCH")}
          onClick={onSearch}
        />
      </div>

      {faqs.length === 0 ? (
        <div className="cuc-empty">{t("CONTACT_US_CENTER.FAQ.EMPTY")}</div>
      ) : (
        faqs.map((f) => {
          const open = openId === f.id;
          const question = getLocalizedField(f, "question", language) || getLocalizedField(f, "title", language);
          const answer = getLocalizedField(f, "answer", language);
          return (
            <div className="cuc-faq-item" key={f.id}>
              <div
                className="cuc-faq-q"
                onClick={() => toggleFaq(f)}
              >
                <span className="cuc-qa-mark q">Q</span>
                <span>{question}</span>
              </div>
              {open && (
                <div className="cuc-faq-a">
                  <span className="cuc-qa-mark a">A</span>
                  <div className="cuc-faq-a-col">
                    <div
                      className="cuc-faq-a-content"
                      dangerouslySetInnerHTML={{ __html: answer || "" }}
                    />
                    <AttachmentDownloads
                      attachments={attachmentsById[f.id] || []}
                      title={question}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default FaqTab;
